import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Coins, Wand2, Box, Video, Layers, Clock, Eye, Zap } from 'lucide-react';
import publicAiTaskCostService from '../../services/publicAiTaskCostService';
import { AITaskCost } from '../../schemas/aiTaskCost';

// Icon mapping for different task types
const getTaskIcon = (taskType: string) => {
  switch (taskType) {
    case 'Image Generation (Basic)':
    case 'text_to_image':
      return { Icon: Wand2, color: 'text-blue-400' };
    case 'Image Generation with Brandify':
    case 'image_processing':
      return { Icon: Layers, color: 'text-purple-400' };
    case 'Image to 3D Model Generation':
    case 'image_to_3d':
      return { Icon: Box, color: 'text-emerald-400' };
    case '3D Model to AR Ad Generation':
    case 'three_d_to_ar_ad':
      return { Icon: Box, color: 'text-teal-400' };
    case 'Image to Video Generation':
    case 'video_generation':
      return { Icon: Video, color: 'text-pink-400' };

    case 'content_generation':
      return { Icon: Wand2, color: 'text-amber-400' };
    case 'Automated Post Scheduling':
    case 'post_scheduling':
      return { Icon: Clock, color: 'text-cyan-400' };
    case 'image_analysis':
      return { Icon: Eye, color: 'text-indigo-400' };
    default:
      return { Icon: Zap, color: 'text-gray-400' };
  }
};

// Display name mapping for better UX
const getDisplayName = (taskType: string): string => {
  const displayNames: Record<string, string> = {
    'Image Generation (Basic)': 'AI image generation',
    'text_to_image': 'AI image generation',
    'Image Generation with Brandify': 'AI image + brand styling',
    'image_processing': 'AI image + brand styling',
    'Image to 3D Model Generation': '3D model creation',
    'image_to_3d': '3D model creation',
    '3D Model to AR Ad Generation': 'AR advertisement',
    'three_d_to_ar_ad': 'AR advertisement',
    'Image to Video Generation': 'video generation',
    'video_generation': 'video generation',

    'content_generation': 'social post creation',
    'Automated Post Scheduling': 'post scheduling',
    'post_scheduling': 'post scheduling',
    'image_analysis': 'image analysis',
  };
  
  return displayNames[taskType] || taskType.toLowerCase().replace(/_/g, ' ');
};

// Priority order for displaying tasks (most important first)
const getTaskPriority = (taskType: string): number => {
  const priorities: Record<string, number> = {
    'Image Generation (Basic)': 1,
    'text_to_image': 1,
    'Image Generation with Brandify': 2,
    'image_processing': 2,
    'Image to 3D Model Generation': 3,
    'image_to_3d': 3,
    'Image to Video Generation': 4,
    'video_generation': 4,
    '3D Model to AR Ad Generation': 5,
    'three_d_to_ar_ad': 5,

    'content_generation': 6,
    'Automated Post Scheduling': 7,
    'post_scheduling': 7,
  };
  
  return priorities[taskType] || 99;
};

interface DynamicCreditExplanationProps {
  maxItems?: number;
  className?: string;
}

const DynamicCreditExplanation: React.FC<DynamicCreditExplanationProps> = ({ 
  maxItems = 3, 
  className = "" 
}) => {
  // Hide credit usage information as requested
  return null;
};

export default DynamicCreditExplanation;